import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DialogService } from 'nebula-ui-kit';
import { TranslationService } from '@nebula-shared/data-access';

import { BankAccountService } from './bank-account.service';
import { SelectDialogComponent } from '../../../select-dialog/select-dialog.component';

export interface BancoEntidadTercero {
  id: number;
  numeroDocumento: string;
  nombreCompleto: string;
  tipoDocumento?: string;
  codigoSupBancaria: string;
  codigoSupBancaria2?: string | null;
  cuentaBancoId?: number;
  banco?: string;
  codCuenta?: string;
  nombreCuenta?: string;
  noIdentificacion?: string;
  numeroCuenta?: string;
}

@Injectable({ providedIn: 'root' })
export class BankEntityModalService {
  private readonly dialogService = inject(DialogService);
  private readonly translation = inject(TranslationService);
  private readonly bankAccountService = inject(BankAccountService);

  private t(key: string): string {
    return this.translation.translate(key);
  }

  listEntidadesBancarias(
    page: number,
    size: number,
    searchTerm?: string,
  ): Observable<{
    data: BancoEntidadTercero[];
    total: number;
  }> {
    return this.bankAccountService.getEntidadesBancariasPaginadas(page, size, searchTerm).pipe(
      map((pageResponse) => ({
        data: pageResponse.content.map((item) => this.toBancoRow(item)),
        total: pageResponse.totalElements,
      })),
      catchError(() => of({ data: [], total: 0 })),
    );
  }

  openSelector(title: string, currentValue?: string): Observable<BancoEntidadTercero | null> {
    const columns = [
      {
        key: 'banco',
        label: 'Banco',
        sortable: true,
        width: '25%',
      },
      {
        key: 'codCuenta',
        label: 'Código Cuenta',
        sortable: true,
        width: '30%',
      },
      {
        key: 'nombreCuenta',
        label: 'Nombre Cuenta',
        sortable: true,
        width: '45%',
      },
    ];

    const loadDataFn = async (
      params: any,
    ): Promise<{ data: BancoEntidadTercero[]; total: number }> => {
      const search = String(params.search || '').trim();
      const page = Math.max(0, (params.page || 1) - 1);
      const size = params.limit || 10;

      const result = await firstValueFrom(this.listEntidadesBancarias(page, size, search));

      return {
        data: result.data,
        total: result.total,
      };
    };

    const dialogRef = this.dialogService.open(SelectDialogComponent, {
      data: {
        title,
        columns,
        multiSelect: false,
        searchPlaceholder: 'Buscar banco...',
        loadDataFn,
        itemIdKey: 'id',
        displayKey: 'nombreCuenta',
        selectedItems: currentValue ? [{ nombreCuenta: currentValue }] : [],
        pageSize: 10,
        pageSizeOptions: [10, 25, 50, 100],
      },
      size: 'lg',
      width: '760px',
    });

    return dialogRef
      .afterClosed()
      .pipe(
        map((selected: BancoEntidadTercero[]) =>
          selected && selected.length > 0 ? selected[0] : null,
        ),
      );
  }

  private toBancoRow(item: any): BancoEntidadTercero {
    return {
      id: Number(item.id) || 0,
      numeroDocumento: String(item.terceroIdentificacion || item.noIdentificacion || ''),
      nombreCompleto: String(item.nombre || item.nombreCuenta || ''),
      codigoSupBancaria: String(item.codigo || item.codCuenta || ''),
      codigoSupBancaria2: null,
      cuentaBancoId: Number(item.id) || 0,
      banco: String(item.terceroNombre || item.banco || ''), //
      codCuenta: String(item.codigo || item.codCuenta || ''),
      nombreCuenta: String(item.nombre || item.nombreCuenta || ''),
      noIdentificacion: String(item.terceroIdentificacion || item.noIdentificacion || ''),
      numeroCuenta: item.numeroCuenta || '',
    };
  }
}
